import { AccountPanelView, LabelListStatementType } from "../../Labeling.types";
import { AccountSelection } from "./AccountSelection";
import clsx from "clsx";
import { useAtom, useSetAtom } from "jotai";
import { NewAccount } from "./NewAccount";
import { AccountPanelViewAtom } from "@/features/Labeling/Labeling.state";
import { useEffect, useState } from "react";
import { PlusIcon } from "@heroicons/react/24/outline";
import { StatementTypeMenu } from "../StatementTypeMenu";
import { useRowSelection } from "../../hooks/useRowSelection";
import RightSidebarButton from "@/features/Navigation/components/RightSidebarButton";
import { rightDrawerOpenAtom } from "@/features/Navigation/state";
import { AccountHintType } from "@/entities/DataAssociation/Draft.model";
import { ACCOUNT_LIST } from "../../constants/labeling.constants";
import { useDraft } from "../../hooks/useDraft";
import { StatementType } from "@/entities/DataAssociation/File.model";
import { BalanceSheetCategory } from "@/entities/DataAssociation/Account.model";
import { useNewAccounts } from "../../hooks/useNewAccounts";

export function AccountPanel() {
    const setSidebarOpen = useSetAtom(rightDrawerOpenAtom);
    const { draft } = useDraft();
    const newAccounts = useNewAccounts();
    const { selectedAccountRow: selectedRow } = useRowSelection();
    const [view, setView] = useAtom(AccountPanelViewAtom);
    const [statementType, setStatementType] = useState<LabelListStatementType>(
        LabelListStatementType.BALANCE_SHEET,
    );
    const category = selectedRow?.account?.label
        ? ACCOUNT_LIST[selectedRow?.account?.label]?.category
        : "undefined";

    useEffect(() => {
        if (selectedRow?.account?.type === AccountHintType.EXISTING) {
            setView(AccountPanelView.ACCOUNT_LIST);
        } else {
            setView(AccountPanelView.NEW_ACCOUNT);
        }
    }, [setView, selectedRow?.account]);

    useEffect(() => {
        if (
            Object.values(BalanceSheetCategory).includes(
                category as BalanceSheetCategory,
            ) ||
            draft.data?.statementType === StatementType.BALANCE_SHEET
        ) {
            setStatementType(LabelListStatementType.BALANCE_SHEET);
        } else {
            setStatementType(LabelListStatementType.INCOME_STATEMENT);
        }
    }, [category, draft.data?.statementType]);

    if (!selectedRow) return null;

    return (
        <div
            className={clsx(
                "px-6",
                "absolute inset-0 flex min-h-0 flex-col overflow-hidden",
            )}
        >
            <div className={clsx("mb-3 mt-6", "flex flex-col", "sticky")}>
                <RightSidebarButton
                    label={
                        view === AccountPanelView.NEW_ACCOUNT
                            ? "Existing accounts"
                            : "Close"
                    }
                    iconDirection={
                        view === AccountPanelView.NEW_ACCOUNT ? "left" : "right"
                    }
                    onClick={
                        view === AccountPanelView.NEW_ACCOUNT
                            ? () => setView(AccountPanelView.ACCOUNT_LIST)
                            : () => setSidebarOpen(false)
                    }
                />
                {view === AccountPanelView.NEW_ACCOUNT && (
                    <>
                        <h2 className="pageheading">New Account</h2>
                        <p className="body-s subdued">
                            Please choose an account name and label.
                        </p>
                    </>
                )}
                {view === AccountPanelView.ACCOUNT_LIST && (
                    <>
                        <h2 className="pageheading">{selectedRow.rowHeader}</h2>

                        <div
                            className={clsx(
                                "mt-5",
                                "flex items-center justify-between",
                            )}
                        >
                            <StatementTypeMenu
                                statementType={statementType}
                                setStatementType={setStatementType}
                            />
                            <button
                                className="quiet capitalize"
                                onClick={() =>
                                    setView(AccountPanelView.NEW_ACCOUNT)
                                }
                            >
                                New Account
                                <PlusIcon width={16} height={16} />
                            </button>
                        </div>
                    </>
                )}
            </div>

            {view === AccountPanelView.ACCOUNT_LIST && (
                <AccountSelection
                    hint={selectedRow.account}
                    statementType={statementType}
                />
            )}

            {view === AccountPanelView.NEW_ACCOUNT && (
                <NewAccount
                    statementType={statementType}
                    setStatementType={setStatementType}
                    hint={selectedRow.account}
                    newAccounts={newAccounts}
                />
            )}
        </div>
    );
}
